import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Default: gradient sky -> deep blue + halo + shine on hover
        default:
          'bg-octa-gradient text-white shadow-octa shadow-octa-glow hover:shadow-octa-lg hover:brightness-110 active:brightness-95',
        // Destructive: solid red, no gradient pra não competir com primary
        destructive:
          'bg-destructive text-destructive-foreground shadow-octa hover:bg-destructive/90',
        // Outline: borda colorida sutil + hover preenche
        outline:
          'border border-input bg-background/50 backdrop-blur-sm shadow-octa hover:bg-accent hover:text-accent-foreground hover:border-primary/40',
        // Secondary: cinza sutil
        secondary: 'bg-secondary text-secondary-foreground shadow-octa hover:bg-secondary/80',
        // Ghost: sem borda, sem fundo
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Link
        link: 'text-primary underline-offset-4 hover:underline',
        // Accent (CTA premium): gradient amber, raro, so pra ações premium
        accent: 'bg-octa-accent text-white shadow-octa hover:brightness-110 active:brightness-95',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
